import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/admin-layout';
import { Card, Table, Th, Td, Badge, Alert } from '../components/ui';
import { getPermissions, type PermissionDef } from '../features/permissions/api';
import { RoleCode } from '@app/shared';

const scopeLabels: Record<string, string> = {
  global: 'Global',
  school: 'Sekolah',
  self: 'Self',
};

function ScopeBadge({ allowed, scope }: { allowed: boolean; scope: string | null }) {
  if (!allowed) return <span style={{ color: '#94A3B8' }}>—</span>;
  return <Badge color="green">{scope ? (scopeLabels[scope] ?? scope) : 'Ya'}</Badge>;
}

export function PermissionMatrixPage() {
  const [permissions, setPermissions] = useState<PermissionDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPermissions()
      .then((data) => {
        setPermissions(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Gagal memuat permission registry');
        setLoading(false);
      });
  }, []);

  const modules = [...new Set(permissions.map((p) => p.module))];

  return (
    <AdminLayout title="Matriks Permission">
      <div style={{ padding: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          🔐 Matriks Permission (Read-Only)
        </h1>
        <p style={{ color: '#64748B', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
          Registry permission baseline V1. Perubahan memerlukan migration tabel permission dan
          persetujuan. Lihat docs/07-RBAC.md RN-01 untuk detail.
        </p>

        {error && <Alert type="error">{error}</Alert>}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748B', padding: '2rem' }}>Memuat...</p>
        ) : (
          modules.map((module) => {
            const modulePerms = permissions.filter((p) => p.module === module);
            return (
              <Card key={module} style={{ marginBottom: '1.5rem' }}>
                <h2
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    marginBottom: '1rem',
                    padding: '1rem 1rem 0',
                  }}
                >
                  {module}
                </h2>
                <Table>
                  <thead>
                    <tr>
                      <Th>Permission</Th>
                      <Th>Deskripsi</Th>
                      <Th style={{ textAlign: 'center' }}>Admin</Th>
                      <Th style={{ textAlign: 'center' }}>Guru</Th>
                      <Th style={{ textAlign: 'center' }}>KS</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {modulePerms.map((perm) => (
                      <tr key={perm.code}>
                        <Td>
                          <code
                            style={{
                              fontSize: '0.8125rem',
                              background: '#F1F5F9',
                              padding: '0.125rem 0.375rem',
                              borderRadius: '0.25rem',
                            }}
                          >
                            {perm.code}
                          </code>
                          <div
                            style={{ fontSize: '0.8125rem', fontWeight: 600, marginTop: '0.25rem' }}
                          >
                            {perm.label}
                          </div>
                        </Td>
                        <Td style={{ fontSize: '0.8125rem', color: '#64748B', maxWidth: '300px' }}>
                          {perm.description}
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <ScopeBadge
                            allowed={perm.roles[RoleCode.ADMIN]?.allowed ?? false}
                            scope={perm.roles[RoleCode.ADMIN]?.scope ?? null}
                          />
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <ScopeBadge
                            allowed={perm.roles[RoleCode.GURU]?.allowed ?? false}
                            scope={perm.roles[RoleCode.GURU]?.scope ?? null}
                          />
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <ScopeBadge
                            allowed={perm.roles[RoleCode.KEPALA_SEKOLAH]?.allowed ?? false}
                            scope={perm.roles[RoleCode.KEPALA_SEKOLAH]?.scope ?? null}
                          />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            );
          })
        )}

        {!loading && permissions.length > 0 && (
          <Card style={{ padding: '1rem' }}>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                fontSize: '0.8125rem',
                color: '#64748B',
              }}
            >
              <span>Legenda:</span>
              {Object.entries(scopeLabels).map(([k, v]) => (
                <span key={k}>
                  <Badge color="green">{v}</Badge> ={' '}
                  {k === 'global'
                    ? 'Semua sekolah/data'
                    : k === 'school'
                      ? 'Sekolah penugasan'
                      : 'Data diri sendiri'}
                </span>
              ))}
              <span>
                <span style={{ color: '#94A3B8' }}>—</span> = Tidak diizinkan
              </span>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
